import { useRef, useState } from 'react';
import {
  Snackbar,
  Paper,
  Box,
  Typography,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { tokens } from '../../global/theme/tokens';
import { useConfig } from '../../api/api';
import { useEmailClient } from './useEmailClient';
import { useUserPreferences } from '../../global/useUserPreferences';
import { reauthenticateInbox } from './helpers/reauthenticate';

const DAY_MS = 24 * 60 * 60 * 1000;
// far-future sentinel used for "ignore forever" (max safe JS date)
const FOREVER = 8640000000000000;
// key under userpreferences.additionalOptions storing per-inbox snooze timestamps
const SNOOZE_KEY = 'mailboxReauthSnooze';

/**
 * App-wide reminder shown when one or more email inboxes need re-authentication.
 * Lists the affected inboxes, lets the user re-authenticate each one directly,
 * and lets them snooze the reminder per inbox (1 day / 30 days / forever),
 * persisted server-side via user preferences.
 */
export const MailboxReauthReminder = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const { reauthInboxes } = useEmailClient();
  const { preferences, updatePreferences } = useUserPreferences();

  // inboxes hidden for this browser session only (via the × button)
  const [sessionDismissed, setSessionDismissed] = useState([]);
  const [ignoreMenuAnchor, setIgnoreMenuAnchor] = useState(null);
  // which ignore duration the split-button will apply (default: 1 day)
  const [selectedIgnoreIndex, setSelectedIgnoreIndex] = useState(0);
  const ignoreGroupRef = useRef(null);

  // wait until preferences have loaded to avoid a flash of already-snoozed items
  if (preferences === null) return null;

  // only surface the reminder on the email client page
  if (!location.pathname.startsWith('/emails')) return null;

  const snoozeMap = preferences?.[SNOOZE_KEY] || {};
  const now = Date.now();

  const visibleInboxes = (reauthInboxes || []).filter((inbox) => {
    if (sessionDismissed.includes(inbox.id)) return false;
    const until = snoozeMap[inbox.id];
    return !until || until <= now;
  });

  if (visibleInboxes.length === 0) return null;

  function handleReauth(inbox) {
    reauthenticateInbox({
      config,
      inboxId: inbox.id,
      onNoAuthUrl: () => navigate('/emails'),
    });
  }

  function snoozeVisible(durationMs) {
    const until = durationMs === FOREVER ? FOREVER : Date.now() + durationMs;
    const next = { ...snoozeMap };
    visibleInboxes.forEach((inbox) => {
      next[inbox.id] = until;
    });
    updatePreferences(SNOOZE_KEY, next);
    setIgnoreMenuAnchor(null);
  }

  function dismissSession() {
    setSessionDismissed((prev) => [
      ...prev,
      ...visibleInboxes.map((inbox) => inbox.id),
    ]);
  }

  const ignoreOptions = [
    { label: t('Ignore for 1 day'), duration: DAY_MS },
    { label: t('Ignore for 30 days'), duration: 30 * DAY_MS },
    { label: t('Ignore forever'), duration: FOREVER },
  ];

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{
        // on mobile span full width and sit above the bottom navigation bar
        left: { xs: 8, sm: 'auto' },
        right: { xs: 8, sm: 24 },
        bottom: { xs: 56, sm: 24 },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: { xs: '100%', sm: 360 },
          maxWidth: '100%',
          p: 2,
          bgcolor: colors.card,
          border: `1px solid ${colors.warning}80`,
          borderRadius: 2,
        }}
      >
        {/* header */}
        <Box className="flex items-start gap-2">
          <WarningAmberIcon sx={{ color: colors.warning, mt: '2px' }} />
          <Box className="flex-1">
            <Typography fontWeight={600} fontSize={15}>
              {t('Mailbox needs re-authentication')}
            </Typography>
            <Typography fontSize={12} sx={{ color: colors.grey[600] }}>
              {t(
                'These inboxes stopped synchronizing. Sign in again to restore them.',
              )}
            </Typography>
          </Box>
          <Tooltip title={t('Dismiss')}>
            <IconButton size="small" onClick={dismissSession} sx={{ mt: '-4px' }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* affected inboxes */}
        <Box className="flex flex-col gap-2 my-3">
          {visibleInboxes.map((inbox) => (
            <Box
              key={inbox.id}
              className="flex items-center justify-between gap-2 rounded-md p-2"
              sx={{ bgcolor: colors.warning + '18' }}
            >
              <Box className="min-w-0">
                <Typography fontSize={13} fontWeight={600} noWrap>
                  {inbox.label}
                </Typography>
                <Typography
                  fontSize={11}
                  noWrap
                  sx={{ color: colors.grey[600], fontStyle: 'italic' }}
                >
                  {`<${inbox.imapUsername}>`}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="info"
                onClick={() => handleReauth(inbox)}
                sx={{ flexShrink: 0 }}
              >
                {t('Re-authenticate')}
              </Button>
            </Box>
          ))}
        </Box>

        {/* ignore split-button — primary applies the selected duration */}
        <Box className="flex justify-end">
          <ButtonGroup
            variant="outlined"
            size="small"
            color="inherit"
            ref={ignoreGroupRef}
          >
            <Button
              onClick={() =>
                snoozeVisible(ignoreOptions[selectedIgnoreIndex].duration)
              }
            >
              {ignoreOptions[selectedIgnoreIndex].label}
            </Button>
            <Tooltip title={t('Change duration')}>
              <Button
                size="small"
                onClick={() => setIgnoreMenuAnchor(ignoreGroupRef.current)}
                sx={{ px: 0.5 }}
              >
                <ArrowDropDownIcon sx={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          </ButtonGroup>
          <Menu
            anchorEl={ignoreMenuAnchor}
            open={Boolean(ignoreMenuAnchor)}
            onClose={() => setIgnoreMenuAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            // raise above the Snackbar (zIndex.snackbar) so options are clickable
            sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
          >
            {ignoreOptions.map((option, index) => (
              <MenuItem
                key={option.label}
                selected={index === selectedIgnoreIndex}
                onClick={() => {
                  setSelectedIgnoreIndex(index);
                  setIgnoreMenuAnchor(null);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Paper>
    </Snackbar>
  );
};

export default MailboxReauthReminder;
